import { FormEvent, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useGetSourceDeploymentConfigQuery } from '@frontend/api-sync-config';
import { Button, NakedButton, SpinningLoader, Text, TextField, useControlledField } from '@frontend/design-system';
import { normalizeSourceDeployment } from '../shared/helpers';
import { SourceDetailView } from './source-detail-view';

interface SourcesPanelProps {
  canManage: boolean;
}

export const SourcesPanel = ({ canManage }: SourcesPanelProps) => {
  const [sourceId, setSourceId] = useState('');
  const [searchedSourceId, setSearchedSourceId] = useState('');
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const sourceIdFieldProps = useControlledField({ type: 'text', value: sourceId, onChange: setSourceId });
  const { data, isFetching, isError, refetch } = useGetSourceDeploymentConfigQuery({
    request: { sourceId: searchedSourceId },
    options: {
      enabled: !!searchedSourceId,
      retry: false,
    },
    httpOptions: { skipValidation: true },
  });
  const sourceConfig = useMemo(() => (data ? normalizeSourceDeployment(data) : undefined), [data]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedSourceId = sourceId.trim();
    if (!trimmedSourceId) return;

    setIsViewingDetails(false);
    if (trimmedSourceId === searchedSourceId) {
      refetch();
      return;
    }

    setSearchedSourceId(trimmedSourceId);
  };

  if (isViewingDetails && sourceConfig) {
    return <SourceDetailView canManage={canManage} config={sourceConfig} onBack={() => setIsViewingDetails(false)} />;
  }

  return (
    <div css={wrapperStyle}>
      <Text as='h2' weight='bold' size='large'>
        Sources
      </Text>
      <form css={searchStyle} onSubmit={handleSearch}>
        <TextField {...sourceIdFieldProps} name='source-id' label='Source ID' placeholder='Search by source ID' />
        <Button type='submit' size='large' disabled={!sourceId.trim() || isFetching}>
          Search
        </Button>
      </form>

      {isFetching ? (
        <div css={statusStyle} role='status'>
          <SpinningLoader size='small' />
          <Text>Searching for source...</Text>
        </div>
      ) : isError ? (
        <Text color='error' role='alert'>
          Failed to search for source ID {searchedSourceId}
        </Text>
      ) : sourceConfig ? (
        <div css={resultsStyle}>
          <Text role='status'>Source ID found.</Text>
          <NakedButton css={sourceCardStyle} type='button' onClick={() => setIsViewingDetails(true)}>
            <span css={detailStyle}>
              <Text size='small' color='subdued'>
                Source ID
              </Text>
              <Text weight='bold'>{sourceConfig.sourceId || searchedSourceId}</Text>
            </span>
            <span css={detailStyle}>
              <Text size='small' color='subdued'>
                Train ID
              </Text>
              <Text weight='bold'>{sourceConfig.targetTrainId || 'N/A'}</Text>
            </span>
          </NakedButton>
        </div>
      ) : null}
    </div>
  );
};

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-lg);
  padding: var(--fabric-space-gap-lg);
`;

const searchStyle = css`
  display: grid;
  grid-template-columns: minmax(240px, 480px) auto;
  align-items: end;
  gap: var(--fabric-space-gap-sm);
  max-width: 640px;
`;

const statusStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
`;

const resultsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-sm);
`;

const sourceCardStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--fabric-space-gap-lg);
  width: 70%;
  padding: var(--fabric-space-padding-lg);
  text-align: left;
  background: var(--fabric-color-neutral-00);
  border: var(--fabric-border-width-thin) solid var(--fabric-color-neutral-20);
  border-radius: var(--fabric-radius-md);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: var(--fabric-color-primary-50);
  }
`;

const detailStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;
